import {Dispatch, ReactNode, SetStateAction, Suspense, useCallback, useState} from 'react';
import {
  BaseUser,
  BodySite,
  DiagnosticReportId,
  ScoringSystemItem,
  Pathology,
  PathologyId,
  ScoringSystem,
  generateRandomId
} from '@legit-health/ui';
import {Box} from '@mui/material';
import {TFunction} from 'i18next';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {AnamnesisQuestion} from '@/shared/interfaces/AnamnesisQuestion';
import AnamnesisStep from '../../steps/AnamnesisStep';
import {CreateDiagnosticReportResponse} from '../../types';
import anamnesisSessionStorage from '../../utils/anamnesisSessionStorage';
import InstructionsStep from './components/Instructions';
import {ScoringSystems, ScoringSystemsSkeleton} from './components/ScoringSystems';
import SendImages from './components/SendImages';

export default function DiagnosisSupportForm({
  anamnesisQuestions,
  bodySiteId,
  currentStep,
  isForClinicalTrials,
  onSuccess,
  patient,
  renderItemLabel,
  setCurrentStep,
  steps,
  t,
  visitIdentifier,
  withTemporaryImages
}: {
  anamnesisQuestions: AnamnesisQuestion[];
  bodySiteId: string;
  currentStep: number;
  isForClinicalTrials: boolean;
  onSuccess: (values: CreateDiagnosticReportResponse) => void;
  patient: BaseUser;
  renderItemLabel?: (options: {
    item: ScoringSystemItem;
    scoringSystem: ScoringSystem;
    bodySite: BodySite;
  }) => string;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  steps: {label: string; icon: ReactNode}[];
  t: TFunction;
  visitIdentifier: string;
  withTemporaryImages: boolean;
}) {
  const {pathologiesById} = useSharedDataContext();
  const [formValues, setFormValues] = useState({
    images: [
      {
        id: generateRandomId(),
        crop: null,
        file: null
      }
    ],
    bodySite: bodySiteId,
    patientId: patient.id,
    visitIdentifier,
    anamnesisQuestions:
      anamnesisSessionStorage.get() ??
      anamnesisQuestions.reduce(
        (acc, aq) => ({
          ...acc,
          [aq.id]: ''
        }),
        {}
      )
  });
  const [diagnosisSupportResult, setDiagnosisSupportResult] = useState<null | {
    pathology: Pathology;
    diagnosticReportId: DiagnosticReportId;
  }>(null);

  const handleFinishQuestionnaires = useCallback(
    function () {
      if (!diagnosisSupportResult) {
        return;
      }
      onSuccess({
        id: diagnosisSupportResult.diagnosticReportId,
        conditionId: null,
        previousDiagnosticReportId: null,
        type: 'diagnosticReport'
      });
    },
    // we do not care of changes in onDiagnosticReportReceived
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diagnosisSupportResult]
  );

  function handleSuccessSendImages({
    firstPathologyId,
    isConclusive,
    id
  }: {
    id: DiagnosticReportId;
    isConclusive?: boolean;
    firstPathologyId?: PathologyId | null;
  }) {
    const pathology = firstPathologyId ? pathologiesById[firstPathologyId] : null;
    if (!pathology || !isConclusive) {
      onSuccess({
        id,
        conditionId: null,
        previousDiagnosticReportId: null,
        type: 'diagnosticReport'
      });
      return;
    }
    setDiagnosisSupportResult({
      pathology,
      diagnosticReportId: id
    });
    setCurrentStep((s) => s + 1);
  }

  let node = null;
  const stepNumberModifier = isForClinicalTrials ? 1 : 0;

  if (currentStep === 1 - stepNumberModifier) {
    node = (
      <AnamnesisStep
        anamnesisQuestions={anamnesisQuestions}
        value={formValues.anamnesisQuestions}
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={function (value) {
          setFormValues((f) => ({
            ...f,
            anamnesisQuestions: value
          }));
          setCurrentStep((s) => s + 1);
        }}
        t={t}
        activeStep={1 - stepNumberModifier}
        totalSteps={steps.length}
      />
    );
  } else if (currentStep === 2 - stepNumberModifier) {
    node = (
      <InstructionsStep
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={() => setCurrentStep((s) => s + 1)}
        t={t}
        activeStep={2 - stepNumberModifier}
        totalSteps={steps.length}
      />
    );
  } else if (currentStep === 3 - stepNumberModifier) {
    node = (
      <SendImages
        formValues={formValues}
        onSuccess={handleSuccessSendImages}
        onBack={() => setCurrentStep((s) => s - 1)}
        t={t}
        activeStep={3 - stepNumberModifier}
        totalSteps={steps.length}
        patient={patient}
        withTemporaryImages={withTemporaryImages}
      />
    );
  } else if (currentStep === 4 - stepNumberModifier && diagnosisSupportResult) {
    node = (
      <Suspense
        fallback={
          <Box
            sx={{
              maxWidth: 'md',
              mx: 'auto'
            }}
          >
            <ScoringSystemsSkeleton />
          </Box>
        }
      >
        <ScoringSystems
          bodySiteId={bodySiteId}
          diagnosticReportId={diagnosisSupportResult.diagnosticReportId}
          onFinish={handleFinishQuestionnaires}
          pathology={diagnosisSupportResult.pathology}
          renderItemLabel={renderItemLabel}
          t={t}
          activeStep={4 - stepNumberModifier}
          totalSteps={steps.length}
        />
      </Suspense>
    );
  }

  return <div>{node}</div>;
}
